/**
 * Helpers shared by the cart page and the checkout (config comes from Checkout::script_data()).
 */

export const config = () => window.wbsFlow || null;

export const endpoint = (name) => config().ajaxUrl.replace('%%endpoint%%', name);

/**
 * POST to a wc-ajax endpoint and return the decoded JSON (null when the request failed).
 */
export const post = async (name, data = {}) => {
    const body = new URLSearchParams();
    Object.keys(data).forEach((key) => body.append(key, data[key]));

    const response = await fetch(endpoint(name), {
        method: 'POST',
        body,
        credentials: 'same-origin',
        headers: {'X-Requested-With': 'XMLHttpRequest'},
    });

    if (response.status === 403) {
        // Expired nonce (page kept open for a long time) - a reload gives a fresh one.
        window.location.reload();
        return null;
    }

    const text = await response.text();

    try {
        return JSON.parse(text);
    } catch (e) {
        // PHP notices printed before the JSON (display_errors on) - parse from the first object.
        const start = text.indexOf('{"');
        if (start > 0) {
            return JSON.parse(text.slice(start));
        }
        throw e;
    }
};

export const debounce = (fn, wait) => {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), wait);
    };
};

/**
 * Replace elements with fresh HTML (same approach as WooCommerce fragments).
 */
export const applyFragments = (fragments) => {
    if (!fragments) {
        return;
    }

    Object.keys(fragments).forEach((selector) => {
        $(selector).replaceWith(fragments[selector]);
    });
};

/**
 * Keep WooCommerce cart fragments (header counter, mini cart) cached in the browser up to date.
 */
export const storeCartFragments = (fragments, cartHash) => {
    const settings = window.settings || {};

    if (!settings.fragment_name || !fragments) {
        return;
    }

    const wcFragments = {};
    Object.keys(fragments).forEach((selector) => {
        if (selector.indexOf('.wbs-') !== 0) {
            wcFragments[selector] = fragments[selector];
        }
    });

    try {
        sessionStorage.setItem(settings.fragment_name, JSON.stringify(wcFragments));
        sessionStorage.setItem(settings.cart_hash_key, cartHash || '');
        localStorage.setItem(settings.cart_hash_key, cartHash || '');
    } catch (e) {
        // Storage can be unavailable (private mode) - fragments are only a cache.
    }
};

/**
 * Small toast with an optional action ("Cofnij"). One at a time, announced to screen readers.
 */
let toastTimer;

export const toast = (message, {action = null, type = 'info', duration = 8000} = {}) => {
    let element = document.querySelector('.wbs-toast');

    if (!element) {
        element = document.createElement('div');
        element.className = 'wbs-toast';
        element.setAttribute('role', 'status');
        element.setAttribute('aria-live', 'polite');
        document.body.appendChild(element);
    }

    element.className = `wbs-toast wbs-toast--${type}`;
    element.innerHTML = '';

    const text = document.createElement('span');
    text.className = 'wbs-toast__text';
    text.textContent = message;
    element.appendChild(text);

    if (action) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'wbs-toast__action';
        button.textContent = action.label;
        button.addEventListener('click', () => {
            hideToast();
            action.onClick();
        });
        element.appendChild(button);
    }

    requestAnimationFrame(() => element.classList.add('is-visible'));

    clearTimeout(toastTimer);
    const schedule = () => {
        clearTimeout(toastTimer);
        toastTimer = setTimeout(hideToast, duration);
    };
    element.onmouseenter = element.onfocusin = () => clearTimeout(toastTimer);
    element.onmouseleave = element.onfocusout = schedule;
    schedule();
};

export const hideToast = () => {
    const element = document.querySelector('.wbs-toast');
    if (element) {
        element.classList.remove('is-visible');
    }
};

export const sprintf = (template, value) => template.replace(/%[sd]/, value);

/**
 * Zdarzenie e-commerce na dataLayer (GA4). Przed każdym zdarzeniem czyścimy obiekt ecommerce,
 * żeby pozycje z poprzedniego nie doklejały się do następnego - tak zaleca dokumentacja GA4.
 */
export const trackEvent = (payload) => {
    if (!payload || !payload.event) {
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ecommerce: null});
    window.dataLayer.push(payload);
};
